import { createApi } from '@reduxjs/toolkit/query/react'

import { Role } from '@/constants/role'
import {
  infinitePaginationQueryOptions,
  InfinitePaginationRequest,
  InfinitePaginationResponse,
} from '@/model/pagination'
import type { Competition } from '@/redux/competition'
import { baseQuery } from '@/redux/utils'
import {
  readAllWallets,
  walletSlice,
} from '@/redux/wallet'

export interface User {
  id: number
  avatar?: string
  introduction?: string
  nickname?: string
  socialLinks: string[]
  role: Role
  wallets: {
    walletId: string
    address: string
  }[]
  followingCount: number
  followedCount: number
  isFollowing: boolean
  isFollowed: boolean
  isMutual: boolean
  ranking: number | null
}

export interface UserStats {
  balance: number
  highestRanking: number
  pnl: number
  points: number
}

export interface ReadUserResponse extends User {
  competitions: Competition[]
  stats: UserStats
}

export type RelatedSimpleUser = Pick<User, 'id' | 'nickname' | 'wallets'> | null

export interface ReadMineUserResponse extends ReadUserResponse {
  activeBotId?: number
}

export interface LoginRequest {
  address: string
  signedMessage: string
  walletId: string
  walletLogo?: string
  walletName: string
}

export interface LoginResponse {
  user: User
  token: string
}

export enum ChallengeType {
  Login = 'login',
  DeleteUser = 'delete',
}

export interface ChallengeRequest {
  address: string
  type: ChallengeType
}

export interface ChallengeResponse {
  message: string
}

export interface RemoveMineUserRequest {
  address: string
  signedMessage: string
}

export interface FollowResponse {
  id: number
  isMutual: boolean
  mineFollowingCount: number
  toFollowedCount: number
}

export interface ReadFollowingResponse {
  rows: User[]
}

export interface ReadFollowedResponse {
  rows: User[]
}

export const readMineUser = 'readMineUser'
export const readOneUser = 'readOneUser'
export const readFollowing = 'readFollowing'
export const readFollowed = 'readFollowed'

export const userSlice = createApi({
  reducerPath: 'user',
  baseQuery,
  tagTypes: [readMineUser, readOneUser, readFollowing, readFollowed],
  endpoints: (builder) => ({
    readMineUser: builder.query<ReadMineUserResponse, void>({
      query: () => '/user/mine',
      providesTags: [readMineUser],
    }),
    readOneUser: builder.query<ReadUserResponse, { id: number }>({
      query: ({ id }) => `/user/${id}`,
      providesTags: [readOneUser],
    }),
    readFollowing: builder.infiniteQuery<
      InfinitePaginationResponse<User | null>,
      void,
      InfinitePaginationRequest
    >({
      query: ({ pageParam }) => ({
        url: '/user/mine/following',
        params: {
          ...pageParam,
        },
      }),
      providesTags: [readFollowing],
      infiniteQueryOptions: infinitePaginationQueryOptions,
    }),
    readFollowed: builder.infiniteQuery<
      InfinitePaginationResponse<User | null>,
      void,
      InfinitePaginationRequest
    >({
      query: ({ pageParam }) => ({
        url: '/user/mine/followed',
        params: {
          ...pageParam,
        },
      }),
      providesTags: [readFollowed],
      infiniteQueryOptions: infinitePaginationQueryOptions,
    }),
    updateMineUser: builder.mutation<User, Partial<User>>({
      query: (request) => ({
        url: '/user/mine',
        method: 'PUT',
        body: request,
      }),
      invalidatesTags: [readMineUser],
    }),
    updateMineWallet: builder.mutation<undefined, LoginRequest>({
      query: (request) => ({
        url: '/user/mine/wallet',
        method: 'PUT',
        body: request,
      }),
      invalidatesTags: [readMineUser],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(walletSlice.util.invalidateTags([readAllWallets]))
      },
    }),
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (request) => ({
        url: '/user/login',
        method: 'POST',
        body: request,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(walletSlice.util.invalidateTags([readAllWallets]))
      },
    }),
    challenge: builder.mutation<ChallengeResponse, ChallengeRequest>({
      query: (request) => ({
        url: '/user/challenge',
        method: 'POST',
        body: request,
      }),
    }),
    removeMineUser: builder.mutation<void, RemoveMineUserRequest>({
      query: (body) => ({
        url: '/user/mine',
        method: 'DELETE',
        body,
      }),
    }),
    follow: builder.mutation<FollowResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/user/${id}/follow`,
        method: 'POST',
      }),
      invalidatesTags: [readFollowing, readFollowed],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const { data: { mineFollowingCount, toFollowedCount, isMutual } } = await queryFulfilled
        dispatch(
          userSlice.util.updateQueryData('readMineUser', undefined, (draft) => {
            draft.followingCount = mineFollowingCount
          }),
        )
        dispatch(
          userSlice.util.updateQueryData('readOneUser', { id }, (draft) => {
            draft.followedCount = toFollowedCount
            draft.isFollowing = true
          }),
        )
        dispatch(
          userSlice.util.updateQueryData('readFollowed', undefined, (draft) => {
            draft.pages.forEach(({ rows }) => {
              rows.forEach((row) => {
                if (row?.id === id) {
                  row.isFollowing = true
                  row.isMutual = isMutual
                }
              })
            })
          }),
        )
      },
    }),
    unfollow: builder.mutation<FollowResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/user/${id}/unfollow`,
        method: 'POST',
      }),
      invalidatesTags: [readFollowing, readFollowed],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const { data: { mineFollowingCount, toFollowedCount } } = await queryFulfilled
        dispatch(
          userSlice.util.updateQueryData('readMineUser', undefined, (draft) => {
            draft.followingCount = mineFollowingCount
          }),
        )
        dispatch(
          userSlice.util.updateQueryData('readOneUser', { id }, (draft) => {
            draft.followedCount = toFollowedCount
            draft.isFollowing = false
          }),
        )
      },
    }),
  }),
})

export const {
  useReadOneUserQuery,
  useLazyReadMineUserQuery,
  useReadFollowingInfiniteQuery,
  useReadFollowedInfiniteQuery,
  useUpdateMineWalletMutation,
  useReadMineUserQuery,
  useLoginMutation,
  useChallengeMutation,
  useUpdateMineUserMutation,
  useRemoveMineUserMutation,
  useFollowMutation,
  useUnfollowMutation,
} = userSlice
